import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

type SpreadPageIndicatorProps = {
  leftText: string
  rightText: string
  hasLeftPage: boolean
  hasRightPage: boolean
  leftLabel: string
  rightLabel: string
  previousDisabled: boolean
  nextDisabled: boolean
  onPrevious: () => void
  onNext: () => void
}

/** Center cluster: previous/next spread buttons around the left | right page titles and numbers. */
export const SpreadPageIndicator: React.FC<SpreadPageIndicatorProps> = ({
  leftText,
  rightText,
  hasLeftPage,
  hasRightPage,
  leftLabel,
  rightLabel,
  previousDisabled,
  nextDisabled,
  onPrevious,
  onNext,
}) => {
  return (
    <div className="flex items-center gap-1 flex-1 min-w-0 justify-center max-w-[504px] min-w-[144px] p-1 overflow-hidden">
      {/* Previous page button */}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Previous spread"
        onClick={onPrevious}
        disabled={previousDisabled}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {/* Center text with separator — | is always centered (aligned with spine at x=0) */}
      <div className="hidden sm:flex items-center gap-1 flex-1 min-w-0 text-xs font-medium leading-4">
        <span className="flex-1 min-w-0 flex items-center gap-1">
          {hasLeftPage && (
            <span
              className="font-sans tabular-nums text-xs font-medium leading-4 shrink-0"
              aria-hidden="true"
            >
              {leftLabel}
            </span>
          )}
          <span className="flex-1 min-w-0 truncate">{leftText?.trim() ?? ''}</span>
        </span>
        <span>|</span>
        <span className="flex-1 min-w-0 flex items-center gap-1">
          <span className="flex-1 min-w-0 truncate">{rightText?.trim() ?? ''}</span>
          {hasRightPage && (
            <span
              className="font-sans tabular-nums text-xs font-medium leading-4 shrink-0"
              aria-hidden="true"
            >
              {rightLabel}
            </span>
          )}
        </span>
      </div>

      {/* Next page button */}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Next spread"
        onClick={onNext}
        disabled={nextDisabled}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}
